import { Eye, Download, FileOutput, Users, FileText, Activity } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { TenantAnalyticsOverview } from '@/lib/api/types'

interface OverviewCardsProps {
  data: TenantAnalyticsOverview | null
  loading?: boolean
}

interface StatCardProps {
  label: string
  value: number
  icon: React.ElementType
  color: string
  trend?: number
  loading?: boolean
}

function StatCard({ label, value, icon: Icon, color, trend, loading }: StatCardProps) {
  return (
    <div className="bg-white dark:bg-[#292929] rounded-xl border border-[#e5e5e5] dark:border-[#3d3d3d] p-5 shadow-[0_2px_4px_rgba(0,0,0,0.04),0_0_2px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.2),0_0_2px_rgba(0,0,0,0.15)]">
      <div className="flex items-start justify-between">
        <div className={cn('p-2.5 rounded-xl bg-gradient-to-br shadow-sm', color)}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        {trend !== undefined && (
          <span
            className={cn(
              'text-xs font-medium px-2 py-1 rounded-full',
              trend >= 0
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            )}
          >
            {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-4">
        {loading ? (
          <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        ) : (
          <p className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
            {value.toLocaleString()}
          </p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
      </div>
      {/* Mini sparkline placeholder */}
      <div className="mt-3 h-8 flex items-end gap-0.5">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex-1 rounded-sm transition-all',
              loading ? 'bg-gray-200 dark:bg-gray-700 animate-pulse' : 'bg-gray-200 dark:bg-gray-600'
            )}
            style={{
              height: loading ? '100%' : `${20 + Math.random() * 80}%`,
            }}
          />
        ))}
      </div>
    </div>
  )
}

export function OverviewCards({ data, loading }: OverviewCardsProps) {
  const stats = [
    {
      label: 'Total Views',
      value: data?.totalViews ?? 0,
      icon: Eye,
      color: 'from-blue-400 to-blue-600',
      trend: data?.viewsTrend,
    },
    {
      label: 'Total Downloads',
      value: data?.totalDownloads ?? 0,
      icon: Download,
      color: 'from-emerald-400 to-green-600',
      trend: data?.downloadsTrend,
    },
    {
      label: 'Documents Generated',
      value: data?.totalGenerations ?? 0,
      icon: FileOutput,
      color: 'from-violet-400 to-purple-600',
      trend: data?.generationsTrend,
    },
    {
      label: 'Unique Users',
      value: data?.uniqueUsers ?? 0,
      icon: Users,
      color: 'from-amber-400 to-orange-600',
    },
    {
      label: 'Total Templates',
      value: data?.totalTemplates ?? 0,
      icon: FileText,
      color: 'from-pink-400 to-rose-600',
    },
    {
      label: 'Active Templates',
      value: data?.activeTemplates ?? 0,
      icon: Activity,
      color: 'from-cyan-400 to-teal-600',
    },
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {stats.map((stat) => (
        <StatCard
          key={stat.label}
          label={stat.label}
          value={stat.value}
          icon={stat.icon}
          color={stat.color}
          trend={stat.trend}
          loading={loading}
        />
      ))}
    </div>
  )
}
